import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../api';
import styles from '../styles/SMSVerification.module.css';

const SMSVerification = ({ user, onUpdate, variant = 'standalone' }) => {
  const isInline = variant === 'inline';
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadVerificationStatus();
  }, []);

  const loadVerificationStatus = async () => {
    try {
      const response = await api.getSMSVerificationStatus();
      setVerificationStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Error loading SMS verification status:', error);
      return null;
    }
  };

  const syncVerifiedUser = (status) => {
    if (onUpdate && status?.isVerified) {
      onUpdate({
        phone_verified_at: status.verifiedAt,
        phone: status.phoneNumber || user?.phone
      });
    }
  };

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleStartVerification = async () => {
    const phoneToVerify = user?.phone?.trim() || '';
    if (!phoneToVerify) {
      showMessage('Add a phone number above before starting verification', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await api.startSMSVerification(phoneToVerify);
      showMessage(response.data.message, 'success');
      await loadVerificationStatus();
    } catch (error) {
      showMessage(error.response?.data?.error || error.response?.data?.message || 'Failed to start verification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteVerification = async () => {
    if (!verificationCode.trim()) {
      showMessage('Please enter the verification code', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await api.completeSMSVerification(verificationCode);
      showMessage(response.data.message, 'success');
      setVerificationCode('');
      const status = await loadVerificationStatus();
      syncVerifiedUser(status);
    } catch (error) {
      showMessage(error.response?.data?.error || error.response?.data?.message || 'Failed to complete verification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelVerification = async () => {
    setLoading(true);
    try {
      const response = await api.cancelSMSVerification();
      showMessage(response.data.message, 'success');
      await loadVerificationStatus();
    } catch (error) {
      showMessage(error.response?.data?.error || error.response?.data?.message || 'Failed to cancel verification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isVerified = verificationStatus?.isVerified;
  const hasPendingVerification = verificationStatus?.hasPendingVerification;
  const containerClass = isInline ? `${styles.smsVerification} ${styles.inline}` : styles.smsVerification;

  return (
    <div className={containerClass}>
      {!isInline && <h4>SMS Verification</h4>}

      {message && (
        <div className={`${styles.message} ${styles[messageType]}`}>
          {message}
        </div>
      )}

      {(() => {
        if (isVerified) {
          return (
            <div className={styles.verifiedStatus}>
              <div className={styles.statusIcon}>✅</div>
              <div className={styles.statusText}>
                <strong>Phone verified</strong>
                <p>
                  {verificationStatus.phoneNumber || user?.phone} is verified.
                  Enable SMS pick reminders in Weekly Lock Reminders below.
                </p>
              </div>
            </div>
          );
        }

        if (hasPendingVerification) {
          return (
            <div className={styles.pendingVerification}>
              <div className={styles.statusIcon}>⏳</div>
              <div className={styles.statusText}>
                <strong>Verification pending</strong>
                <p>Enter the 6-digit code sent to {user?.phone || verificationStatus?.phoneNumber}.</p>
              </div>

              <div className={styles.verificationForm}>
                <label htmlFor="sms-verification-code">Verification code:</label>
                <input
                  id="sms-verification-code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="6-digit code"
                  maxLength="6"
                  disabled={loading}
                />
                <div className={styles.buttonGroup}>
                  <button
                    onClick={handleCompleteVerification}
                    disabled={loading || !verificationCode.trim()}
                    className={styles.primaryButton}
                  >
                    {loading ? 'Verifying...' : 'Complete Verification'}
                  </button>
                  <button
                    onClick={handleCancelVerification}
                    disabled={loading}
                    className={styles.secondaryButton}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className={styles.notVerified}>
            <div className={styles.statusIcon}>📱</div>
            <div className={styles.statusText}>
              <strong>Phone not verified</strong>
              <p>
                {user?.phone
                  ? `We'll send a code to ${user.phone}. Change the number above first if needed.`
                  : 'Add a phone number above, then start verification for SMS reminders and password reset.'}
              </p>
            </div>

            <div className={styles.verificationForm}>
              <button
                onClick={handleStartVerification}
                disabled={loading || !user?.phone?.trim()}
                className={styles.primaryButton}
              >
                {loading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </div>
          </div>
        );
      })()}

      {!isInline && (
        <div className={styles.helpText}>
          <p><strong>How it works:</strong></p>
          <ol>
            <li>Save your phone number in Profile above</li>
            <li>Click &quot;Send Verification Code&quot; — you&apos;ll receive a 6-digit SMS</li>
            <li>Enter the code to verify</li>
            <li>Enable SMS reminders in Weekly Lock Reminders</li>
          </ol>
        </div>
      )}
    </div>
  );
};

SMSVerification.propTypes = {
  user: PropTypes.object,
  onUpdate: PropTypes.func,
  variant: PropTypes.oneOf(['standalone', 'inline'])
};

export default SMSVerification;
